import type { CashPoolProduct } from '@/types/cash-pool.types';

/**
 * Which Collateral Manager workspace a given trade belongs to (LR-587).
 *
 * The 12.08 structure is agreement → collateral leg, and a trade answers both
 * questions itself: a repo/reverse lives on the GMRA margin-call view, a
 * rebate-mode GMSLA trade on Cash Rebate, and everything else on Cash Pool Fee.
 *
 * An ABSENT mode reads as rebate, not as fee: `RateDetails.rate_entry_mode`
 * declares `default='rebate'`, so that is what the backend considers a row it
 * never received a mode for. The column is NOT NULL and the list serializer
 * carries it, so this only bites a trimmed projection — but disagreeing with the
 * model there would file the trade under a leg the backend does not put it in.
 * `MtmPage` splits the book on the same rule.
 *
 * One helper because THREE surfaces navigate a trade into that workspace — the
 * blotter's row action, the single-trade view's "View in MTM", and the
 * origin-aware back link on the trade header — and a copy that disagreed would
 * land the operator on a subtab their trade is not in, which reads as an empty
 * grid rather than as a wrong link.
 */
export type CollateralLeg = 'cash' | 'fee' | 'rebate';

export interface CollateralSearch {
  product: CashPoolProduct;
  leg: CollateralLeg;
  cp: number | undefined;
}

interface TradeWorkspaceFields {
  trade_type?: string | null;
  rate_entry_mode?: string | null;
}

export const collateralSearchForTrade = (
  trade: TradeWorkspaceFields,
  cp: number | undefined,
): CollateralSearch => {
  if (trade.trade_type === 'repo' || trade.trade_type === 'reverse_repo') {
    return { product: 'gmra', leg: 'cash', cp };
  }
  return {
    product: 'gmsla',
    leg: trade.rate_entry_mode === 'fee' ? 'fee' : 'rebate',
    cp,
  };
};
